"use client"

import { usePathname, useSearchParams } from "next/navigation"
import { useEffect, Suspense, useState } from "react"
import { usePostHog, PostHogProvider as PHProvider } from "posthog-js/react"

import posthog from "posthog-js"
import { usePosthogIdentified } from "@/hooks/usePosthogIdentified"
import { identifyAuthenticatedPostHogUser } from "@/lib/posthog/identity"
import { registerEmbeddedStandaloneWebphoneDiagnostics } from "@/lib/posthog/embeddedDiagnostics"
import { getPostHogPersistence, shouldForcePostHogSessionRecording, toPostHogRuntimeContext } from "@/lib/posthog/runtime"
import type { TLoginUserData } from "@/schemas/user.schema"

type PostHogLogsConfig = {
  logs?: {
    captureConsoleLogs?: boolean
    serviceName?: string
    environment?: string
    serviceVersion?: string
  }
}

function buildPostHogPageViewUrl(pathname: string, search: string): string {
  const url = new URL(pathname || "/", window.location.origin)
  if (search) {
    url.search = search
  }
  return url.toString()
}

export function PostHogProvider({ children, initialUserData }: { children: React.ReactNode; initialUserData?: TLoginUserData }) {
  const [isPostHogReady, setIsPostHogReady] = useState(false)
  const [initialPageViewUrl, setInitialPageViewUrl] = useState<string | null>(null)

  useEffect(() => {
    const runtimeContext = toPostHogRuntimeContext(window)
    let hasRegisteredDiagnostics = false
    let diagnosticsCleanup = () => {}

    const markReady = (client: typeof posthog) => {
      if (!hasRegisteredDiagnostics) {
        diagnosticsCleanup = registerEmbeddedStandaloneWebphoneDiagnostics(client, runtimeContext)
        hasRegisteredDiagnostics = true
      }

      if (initialUserData?.user.id && initialUserData.user.id > 0) {
        identifyAuthenticatedPostHogUser({
          account: initialUserData.account,
          client,
          storage: window.localStorage,
          user: initialUserData.user,
        })
      }

      const currentUrl = buildPostHogPageViewUrl(window.location.pathname, window.location.search)
      client.capture("$pageview", { $current_url: currentUrl })
      setInitialPageViewUrl(currentUrl)
      setIsPostHogReady(true)
    }

    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com",
      person_profiles: "identified_only", // or 'always' to create profiles for anonymous users as well
      capture_pageview: false, // Disable automatic pageview capture, as we capture manually
      capture_pageleave: true, // Enable pageleave capture
      capture_heatmaps: true, // Enable heatmap capture
      capture_exceptions: true, // Enable exception capture
      capture_dead_clicks: true, // Enable dead click capture
      capture_performance: true, // Enable performance capture
      enable_heatmaps: true, // Enable heatmap capture
      enable_recording_console_log: true, // Enable recording console log
      persistence: getPostHogPersistence(runtimeContext),
      session_recording: {
        // WARNING: Only enable this if you understand the security implications
        recordCrossOriginIframes: true,
      },
      ...(process.env.NEXT_PUBLIC_POSTHOG_LOGS_ENABLED === "true"
        ? ({
            logs: {
              captureConsoleLogs: false,
              serviceName: process.env.NEXT_PUBLIC_NAME || "ringio-front-web",
              environment: process.env.NEXT_PUBLIC_ENV || process.env.NODE_ENV || "unknown",
              serviceVersion: process.env.NEXT_PUBLIC_VERSION || "unknown",
            },
          } satisfies PostHogLogsConfig)
        : {}),
      defaults: "2026-01-30",
      loaded: (client) => {
        markReady(client as typeof posthog)
      },
    })

    if (shouldForcePostHogSessionRecording(runtimeContext)) {
      posthog.startSessionRecording(true)
    }

    if ("__loaded" in posthog && posthog.__loaded) {
      markReady(posthog)
    }

    return () => {
      diagnosticsCleanup()
    }
  }, [initialUserData])

  return (
    <PHProvider client={posthog}>
      {isPostHogReady ? <SuspendedPostHogPageView initialPageViewUrl={initialPageViewUrl} /> : null}
      {children}
    </PHProvider>
  )
}

function PostHogPageView({ initialPageViewUrl }: { initialPageViewUrl: string | null }) {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const posthog = usePostHog()

  usePosthogIdentified()

  // Track pageviews
  useEffect(() => {
    if (pathname && posthog) {
      const url = buildPostHogPageViewUrl(pathname, searchParams.toString())
      if (url === initialPageViewUrl) {
        return
      }

      posthog.capture("$pageview", { $current_url: url })
    }
  }, [initialPageViewUrl, pathname, searchParams, posthog])

  return null
}

// Wrap PostHogPageView in Suspense to avoid the useSearchParams usage above
// from de-opting the whole app into client-side rendering
// See: https://nextjs.org/docs/messages/deopted-into-client-rendering
function SuspendedPostHogPageView({ initialPageViewUrl }: { initialPageViewUrl: string | null }) {
  return (
    <Suspense fallback={null}>
      <PostHogPageView initialPageViewUrl={initialPageViewUrl} />
    </Suspense>
  )
}
